import React, { useState, useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import { Typography, Tooltip } from '@mui/material';
import { TooltipProps } from '@mui/material/Tooltip';
import { THERAPY_ADMIN_STATUS_ON_HOLD } from 'constants/index';
import FetchText from 'components/form/field/fetch-text';
import HTTP from 'services/http';

import { IDosingInformationTooltipContent } from '../interfaces';
import { DosingTooltipStyled } from './tooltip.styles';

export const DosingInformationTooltipContent = ({
  therapies,
  dosingInformation,
}: IDosingInformationTooltipContent) => {
  const sortedTherapies = useMemo(
    () =>
      [...(therapies ?? [])].sort((a, b) => (a.drug_name ?? '').localeCompare(b.drug_name ?? '')),
    [therapies],
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedTherapy = sortedTherapies?.[selectedIndex] || dosingInformation;
  const cummulativeDeltaY = useRef(0);

  const {
    diagnosis_code: diagnosisCode,
    diagnosis_description: diagnosisDescription,
    start_date: startDate,
    on_hold_date: onHoldDate,
    dosis_regimen: dosisRegimen,
    administration_status,
    last_csl_completed_dt: lastCslCompletedDate,
    drug_name,
    strength,
    strength_unit_of_measure,
    dosage_form,
  } = selectedTherapy;

  const [prescriberName, setPrescriberName] = useState<string | React.ReactElement>('-');
  const internalContentRef = useRef<HTMLDivElement | null>(null);

  const fetchPhysician = (npi: string) => {
    setPrescriberName(
      npi ? (
        <FetchText
          promise={() => HTTP.get(`providers/physician/${npi}`, {})}
          // eslint-disable-next-line no-confusing-arrow
          parse={(result: any) => {
            const credential = result.data.credential ? `, ${result.data.credential}` : '';
            return result && result.data && typeof result.data.full_name === 'string'
              ? `${result.data.full_name.toLowerCase()}${credential} - NPI (${npi})`
              : '-';
          }}
        />
      ) : (
        '-'
      ),
    );
  };

  useEffect(() => {
    if (selectedTherapy.prescribing_physician_npi) {
      fetchPhysician(selectedTherapy.prescribing_physician_npi);
    }
  }, [selectedTherapy.prescribing_physician_npi, selectedIndex]);

  useEffect(() => {
    const element = internalContentRef.current;
    if (!element) return;

    const handleWheel = (event: WheelEvent) => {
      const movementY = Math.abs(event.deltaY) < 5 ? event.deltaY / 10 : event.deltaY; // Normalize small scrolls
      cummulativeDeltaY.current += movementY;
      if (Math.abs(cummulativeDeltaY.current) < 5) {
        return; // Ignore small scrolls until cumulative delta exceeds threshold
      }
      cummulativeDeltaY.current = 0; // Reset cumulative delta after processing
      event.preventDefault();
      event.stopPropagation();
      setSelectedIndex(prevIndex => {
        const totalTherapies = therapies?.length ?? 0;
        if (totalTherapies === 0) {
          return prevIndex;
        }

        let nextIndex = prevIndex + (cummulativeDeltaY.current > 0 ? 1 : -1);
        if (nextIndex < 0) {
          nextIndex = totalTherapies - 1;
        } else if (nextIndex >= totalTherapies) {
          nextIndex = 0;
        }

        return nextIndex;
      });
    };

    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => element.removeEventListener('wheel', handleWheel);
  }, []);

  const fullDrugName = [drug_name, strength, strength_unit_of_measure, dosage_form]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={internalContentRef}
      style={{
        maxHeight: 320,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
      className="scrollable-no-scrollbar"
    >
      <section>
        <Typography>{fullDrugName}</Typography>
      </section>
      <hr />
      <section>
        <Typography className="info bold">Primary ICD-10 Code:</Typography>
        <Typography className="info">
          {diagnosisCode && diagnosisDescription
            ? `${diagnosisCode} - ${diagnosisDescription}`
            : '-'}
        </Typography>
      </section>

      <hr />

      <section>
        <Typography className="info bold">Start Date:</Typography>
        <Typography className="info">
          {startDate ? moment(startDate).utc().format('MM/DD/YYYY') : '-'}
        </Typography>
      </section>

      <hr />

      {administration_status === THERAPY_ADMIN_STATUS_ON_HOLD && (
        <>
          <section>
            <Typography className="info bold">On Hold Date:</Typography>
            <Typography className="info">
              {onHoldDate ? moment(onHoldDate).utc().format('MM/DD/YYYY') : '-'}
            </Typography>
          </section>
          <hr />
        </>
      )}
      <section>
        <Typography className="info bold">Last CSL Completed Date:</Typography>
        <Typography className="info">
          {lastCslCompletedDate ? moment(lastCslCompletedDate).utc().format('MM/DD/YYYY') : '-'}
        </Typography>
      </section>
      <hr />
      <section>
        <Typography className="info bold">Dosing Regimen:</Typography>
        <Typography className="info">{dosisRegimen || '-'}</Typography>
      </section>

      <hr />
      <section>
        <Typography className="info bold">Prescriber:</Typography>
        <Typography className="info">{prescriberName}</Typography>
      </section>
    </div>
  );
};

const StyledDosingInformationTooltip = DosingTooltipStyled(Tooltip);

export const DosingInformationTooltip = ({
  children,
  title,
  ...tooltipProps
}: TooltipProps): JSX.Element => {
  return (
    <StyledDosingInformationTooltip title={title} {...tooltipProps}>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{children}</span>
    </StyledDosingInformationTooltip>
  );
};
